import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import jsonpath from 'jsonpath';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faCloudArrowUp, faEraser, faCopy } from '@fortawesome/free-solid-svg-icons';
import SmallModal from './../components/SmallModal'
import { Link } from 'react-router-dom';

const exampleJson = {
  "description": "Book Store",
  "book": [
    {
      "author": "Nigel Rees",
      "title": "Sayings of the Century",
      "price": 8.95
    },
    {
      "author": "Evelyn Waugh",
      "title": "Sword of Honour",
      "price": 12.99
    },
    {
      "title": "Moby Dick",
      "price": 8.99
    },
  ],
};

const JsonQuery = (props) => {
  const { editorTheme } = props
  const [originalInput, setOriginalinput] = useState(JSON.stringify(exampleJson, null, 2));
  const [jsonQueryInput, setJsonQueryInput] = useState('$.book[*].title');
  const [stringOutput, setStringOutput] = useState('');

  const handleQuery = () => {
    try {
      const result = jsonpath.query(JSON.parse(originalInput), jsonQueryInput);
      setStringOutput(JSON.stringify(result, null, 2));
    } catch (error) {
      console.error(error);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setOriginalinput(content);
    };
    reader.readAsText(file);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(stringOutput);
  }

  const tipsContent = () => {
    return (
      <p className="text-lef">
        <b className='text-gray-500 dark:text-gray-300'>Example usage:</b> <br/><br/>
        <code class="language-javascript">
          $.book[*].title → titles of all the books <br/>
          $.book[?(@.price &gt; 10)] → books with price &gt; 10<br/>
          $.book[?(@.author)] → books with an author <br/>
          $.book[0,1] → first two books
        </code>
        <Link target='_blank' className='flex font-medium text-blue-600 dark:text-blue-500 hover:underline' to='https://github.com/json-path/JsonPath?tab=readme-ov-file'>Checkout Document</Link>
      </p>
    )
  }

  useEffect(() => {
    if (originalInput === "" || jsonQueryInput === "") {
      setStringOutput("");
    }
  }, [originalInput, jsonQueryInput])

  return (
    <div className="grid grid-cols-1 my-4 xl:grid-cols-2 xl:gap-4">
      <div className="col-span-2 xl:col-auto">
        <div className="p-4 mb-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
          <div className="px-4 py-2 text-gray-400 border border-gray-200 border-dashed rounded dark:border-gray-600">
            <h3 className='uppercase border-l-4 border-blue-600 p-2'>JSON Input</h3>
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
            <Editor 
              height="40vh"
              value={originalInput}
              onChange={(value, event) => setOriginalinput(value)}
              placeholder="Please enter JSON code."
              defaultLanguage="json"
              defaultValue={originalInput}
              theme={editorTheme}
              className="block p-2.5 w-full text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            />
          </div>
          <div className="px-4 py-2 text-gray-400rounded dark:border-gray-600">
            <input
              type="file"
              accept='.json'
              onChange={handleFileUpload}
              className='hidden'
              name='file1'
            />
            <button
              type="button"
              onClick={() => document.querySelector('input[name="file1"]').click()}
              className="text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
            >
              <FontAwesomeIcon icon={faCloudArrowUp}/>
              &nbsp; Browse your JSON file
            </button>
            &nbsp;&nbsp;
            <button
              onClick={() => setOriginalinput('')}
              type="button"
              className="text-white bg-gray-400 hover:bg-gray-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
            >
              <FontAwesomeIcon icon={faEraser}/>
              &nbsp; Reset
            </button>
          </div>
        </div>
      </div>
      <div className="col-span-2 xl:col-auto">
        <div className="p-4 mb-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
          <div className="px-4 py-2 text-gray-400 border border-gray-200 border-dashed rounded dark:border-gray-600">
            <h3 className='uppercase border-l-4 border-yellow-600 p-2'>Query</h3>
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
            <Editor 
              height="40vh"
              value={jsonQueryInput}
              placeholder='Enter the JSON path'
              onChange={(value, event) => setJsonQueryInput(value.trim())}
              language="text"
              defaultValue={jsonQueryInput}
              theme={editorTheme}
              className="block p-2.5 w-full text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            />
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600 text-right">
            <SmallModal title='JSON Query Tips' content={tipsContent()} />
            <button
              onClick={() => setJsonQueryInput('')}
              type="button"
              className="text-white bg-gray-400 hover:bg-gray-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
            >
              <FontAwesomeIcon icon={faEraser}/>
              &nbsp; Reset
            </button>
            &nbsp;&nbsp;
            <button
              onClick={handleQuery}
              type="button"
              className="text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
            >
              <FontAwesomeIcon icon={faPaperPlane}/>
              &nbsp; Run
            </button>
          </div>
        </div>
      </div>
      <div className="col-span-2">
        <div className="p-4 mb-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
          <div className="px-4 py-2 text-gray-400 border border-gray-200 border-dashed rounded dark:border-gray-600">
            <h3 className='uppercase border-l-4 border-green-600 p-2'>JSON Query Result</h3>
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
            <Editor 
              height="40vh"
              value={stringOutput}
              placeholder='JSON query result here'
              language="json"
              defaultValue={''}
              theme={editorTheme}
              className="block p-2.5 w-full text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            />
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
            <button
              type="button"
              onClick={handleCopy}
              className="text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
            >
              <FontAwesomeIcon icon={faCopy}/>
              &nbsp; Copy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonQuery;
